import { formatUnits } from 'viem'
import { dolomiteConfigs } from '@1delta/data-sdk'
import { DolomiteMarginAbi } from '@1delta/abis'
import { createMarketUid } from '../../../utils'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'

const LENDER = 'DOLOMITE'

export interface DolomiteOracleMeta {
  markets: { marketId: string; token: string }[]
}

/**
 * One `getMarketPrice(marketId)` call per market. Dolomite oracle prices are
 * already USD-denominated, so there is no base-asset dependency — this fetcher
 * can run in the independent slot (no need to wait for Aave/base prices).
 */
export function getDolomiteCalls(
  chainId: string,
): FetcherResult<DolomiteOracleMeta>[] {
  const cfg = dolomiteConfigs()?.[chainId]
  const margin = cfg?.dolomiteMargin
  const marketsMap = cfg?.markets
  if (!margin || !marketsMap) return []

  const markets = Object.entries(marketsMap).map(([marketId, token]) => ({
    marketId,
    token: String(token).toLowerCase(),
  }))
  if (markets.length === 0) return []

  const calls: MulticallCall[] = markets.map(({ marketId }) => ({
    address: margin,
    name: 'getMarketPrice',
    params: [marketId],
  }))

  return [{ calls, meta: { markets }, lender: LENDER }]
}

/**
 * Dolomite price scale: `getMarketPrice` returns `USD * 10^(36 - tokenDecimals)`
 * (so `price * weiAmount = USD * 1e36`). Hence `priceUSD = value / 10^(36 - dec)`.
 */
export function parseDolomiteResults(
  data: any[],
  meta: DolomiteOracleMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, tokenList } = context
  const entries: OraclePriceEntry[] = []

  meta.markets.forEach((mkt, i) => {
    const raw = data[i]
    if (raw === undefined || raw === '0x') return
    const asset = mkt.token
    const decimals = tokenList?.[asset]?.decimals ?? 18
    try {
      const value = BigInt((raw?.value ?? raw).toString())
      const priceUSD = Number(formatUnits(value, 36 - decimals))
      if (!isFinite(priceUSD) || priceUSD === 0) return
      entries.push({
        asset,
        price: priceUSD,
        priceUSD,
        marketUid: createMarketUid(chainId, LENDER, String(mkt.marketId)),
        targetLender: LENDER,
      })
    } catch {
      // skip invalid price
    }
  })

  return entries
}

export function getDolomiteAbi() {
  return DolomiteMarginAbi
}

/**
 * Dolomite Oracle Fetcher module
 */
export const dolomiteFetcher = {
  getCalls: getDolomiteCalls,
  parse: parseDolomiteResults,
  getAbi: getDolomiteAbi,
}
